import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TilesGrid, TilesGridCellImage, TilesGridCellSelectionDecorator, TilesGridCellDecorativeLabel } from '@bingads-webui-campaign-react/asset-library-components';
import { CustomFluentUIIcons } from '@bingads-webui-campaign-react/asset-library-resources';

const gap = 8;
const marginToSelector = 2;
const imageCellWidth = 295 + (gap * 2) + (marginToSelector * 2);
const imageCellHeight = 243 + (gap * 2) + (marginToSelector * 2);

export const MaxImageCount = 4;

const addSearchParams = (url, params = {}) =>
  new URL(`${url.origin}${url.pathname}?${new URLSearchParams([
    ...Array.from(url.searchParams.entries()),
    ...Object.entries(params),
  ])}`);

const LoadingCell = ({ classes, isLoading }) => (
  <div className={classNames('image-grid-cell-container', classes.imageGridCellContainer)}>
    <div className={classNames('tiles-grid-cell-content', classes.tilesGridCellContent)} >
      <div className={classNames('cell-content', classes.cellContent)} >
        <div className={classNames('loading-placeholder', classes.loadingPlaceholder)}>
          <div className={classNames('loading-image', classes.loadingImage, isLoading ? classes.loadingImageAnimation : null)} />
        </div>
      </div>
    </div>
  </div>);

const GeneratedImages = ({
  images, onSelectImage, i18n, classes, isLoading,
}) => {
  const onRenderLoadingCell = useCallback(() => (
    <LoadingCell classes={classes} isLoading={isLoading} />
  ), [classes, isLoading]);

  const onRenderImageSpinner = useCallback(() => (
    <div className={classNames('loading-placeholder', classes.loadingPlaceholder)}>
      <div className={classNames('loading-image', classes.loadingImage, classes.loadingImageAnimation)} />
    </div>
  ), [classes]);

  const onKeyDown = useCallback((event, i) => {
    const imageContainers = document.querySelectorAll('.actionImages .image-grid-cell-container');
    const footerButton = document.querySelector('.ms-Panel-footer button:not([disabled])');
    const headerButtons = document.querySelectorAll('.ai-generate-image-view .searchbar button:not([disabled]), .ms-Panel-commands button:not([disabled])');
    const headerButton = headerButtons && headerButtons.length && headerButtons[headerButtons.length - 1];
    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowLeft':
        if (i > 0 && imageContainers[i - 1]) {
          imageContainers[i - 1].focus();
        }
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        if (i < imageContainers.length - 1 && imageContainers[i + 1]) {
          imageContainers[i + 1].focus();
        }
        break;
      case 'Tab':
        if (event.shiftKey && headerButton) {
          headerButton.focus();
        } else if (footerButton) {
          footerButton.focus();
        }
        break;
      case 'Enter':
        onSelectImage(i);
        break;
      default:
        break;
    }

    event.stopPropagation();
    event.preventDefault();
  }, [onSelectImage]);

  const onRenderCell = useCallback(({ item, index }) => {
    let {
      thumbnailSrc: src,
    } = item;

    const {
      isSelected: selected = false,
    } = item;

    try {
      let srcURL = new URL(src);
      if (srcURL.host === 'th.bing.com') {
        const length = 330;
        srcURL = addSearchParams(srcURL, { w: length, h: length });
        src = srcURL.toString();
      }
    } catch (e) {
      // ignore
    }

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className={classNames('image-grid-cell-container', classes.imageGridCellContainer)}
        onKeyDown={(event) => { onKeyDown(event, index); }}
        data-is-focusable
      >
        <div className={classNames('tiles-grid-cell-content', classes.tilesGridCellContent)}>
          <div className={classNames('cell-content', classes.cellContent, classes.cellContentShadow, selected && classes.selectedCellContent)}>
            <TilesGridCellSelectionDecorator selected={selected} onClick={() => onSelectImage(index)} item={item}>
              <TilesGridCellImage
                src={src}
                i18n={i18n}
                alt={i18n.getString(_TL_('AI generated image'))}
                showSpinner
                onCustomizedSpinner={onRenderImageSpinner}
              />
              <div className={classes.imageCellLabelContainer}>
                <TilesGridCellDecorativeLabel
                  iconName={CustomFluentUIIcons.SparkeSVGIcon}
                  classes={{ iconTextWrapper: 'created-with-ai-label' }}
                />
              </div>
            </TilesGridCellSelectionDecorator>
          </div>
        </div>
      </div>
    );
  }, [classes.imageGridCellContainer, classes.tilesGridCellContent, classes.cellContent, classes.cellContentShadow, classes.selectedCellContent, classes.imageCellLabelContainer, i18n, onRenderImageSpinner, onKeyDown, onSelectImage]);


  return (<TilesGrid
    items={images}
    itemKey="thumbnailSrc"
    totalItemCount={isLoading ? MaxImageCount : ((images && images.length) || 0)}
    onRenderLoadingCell={onRenderLoadingCell}
    onRenderCell={onRenderCell}
    config={{
      maxColumnWidth: imageCellWidth,
      minColumnWidth: 250,
      rowHeight: imageCellHeight,
      preferredCellsPerPage: MaxImageCount,
    }}
  />);
};

GeneratedImages.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onSelectImage: PropTypes.func.isRequired,
  classes: PropTypes.shape({
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export {
  GeneratedImages,
  gap,
  imageCellHeight,
  imageCellWidth,
  marginToSelector
};
